import {
  Box,
  BoxProps,
  Flex,
  Heading,
  Icon,
  SingleSelect,
  smBumps,
  Status,
  Typo,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useMemo } from 'react'

import {
  TKeyBuilder,
  tProduct,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { getHelpContentBaseUrl, useConfig } from '../../../../state/configuration'
import { useProduct } from '../../../product/ProductProvider'
import { HelpFlexAnchor } from '../../links'

export interface CoverLevelSelectionProps extends BoxProps {
  tKey: TKeyBuilder
  coverLevels: string[]
  value?: string
  onChange: (coverLevel: string) => void
  loading?: boolean
}

const key = 'cover-levels'

export const CoverLevelSelection: FunctionComponent<CoverLevelSelectionProps> = ({
  coverLevels,
  onChange,
  value,
  loading,
  tKey,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const {
    product: {
      coverLevels: { helpLink },
    },
  } = useProduct()

  const contentBaseUrl = useConfig(getHelpContentBaseUrl)
  const header = t(tKey(key, 'header'), { defaultValue: null })

  const selectOptions = useMemo(
    () =>
      coverLevels.length > 1
        ? coverLevels.reduce(
            (acc, level) => ({
              ...acc,
              [level]: <Option level={level} tKey={tKey} />,
            }),
            {},
          )
        : {},
    [coverLevels, t],
  )

  return coverLevels.length > 1 ? (
    <Box display='flex' flexDirection='column' gap={smBumps} width={1} {...props}>
      {header && (
        <Flex width={1}>
          <Heading variant='h4' as='h4' typoSize='md'>
            {header}
          </Heading>
          {helpLink && (
            <HelpFlexAnchor
              variant='highlight'
              borderRadius='50%'
              justifyContent='center'
              ml={smBumps}
              p={1}
              href={`${contentBaseUrl}/${key}.html`}
            >
              <Icon icon='help' size='medium' />
            </HelpFlexAnchor>
          )}
        </Flex>
      )}
      <SingleSelect
        name={'coverLevelSelection'}
        options={selectOptions}
        value={value}
        onChange={onChange}
        width={1}
        disabled={loading}
      />
    </Box>
  ) : null
}

const Option: FunctionComponent<{
  level: string
  tKey: TKeyBuilder
}> = ({ level, tKey }) => {
  const { t } = useWriskTranslation()

  const {
    product: {
      coverLevels: { levels },
    },
  } = useProduct()

  const label = t(tProduct(key, level, 'label'))

  const isRecommended = levels.some((it) => it.coverLevel === level && it.recommended)

  return isRecommended ? (
    <Flex width={1} justifyContent='space-between' gap={2}>
      <Typo>{label}</Typo>
      <Status variant='info' typoSize='xs' fontWeight='bold'>
        {t(tKey(key, 'recommended'))}
      </Status>
    </Flex>
  ) : (
    label
  )
}
